import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { getUnitsWithProgress, syncStudentProgress } from '@/lib/db'
import type { UnitWithProgress, UnitStatus } from '@/types'
import {
  Lock,
  CheckCircle,
  PlayCircle,
  Calendar,
  FileText,
  ChevronRight,
  Trophy,
  Star,
  Clock
} from 'lucide-react'

const statusColors: Record<UnitStatus, { bg: string; text: string; border: string }> = {
  locked: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-300' },
  available: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
  in_progress: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300' },
  workshop_pending: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300' },
  exam_pending: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300' },
  completed: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300' }
}

const statusLabels: Record<UnitStatus, string> = {
  locked: 'Bloqueada',
  available: 'Disponible',
  in_progress: 'En Progreso',
  workshop_pending: 'Taller Pendiente',
  exam_pending: 'Examen Pendiente',
  completed: 'Completada'
}

const statusIcons: Record<UnitStatus, React.ReactNode> = {
  locked: <Lock className="w-5 h-5" />,
  available: <PlayCircle className="w-5 h-5" />,
  in_progress: <PlayCircle className="w-5 h-5" />,
  workshop_pending: <Calendar className="w-5 h-5" />,
  exam_pending: <FileText className="w-5 h-5" />,
  completed: <CheckCircle className="w-5 h-5" />
}

export default function Units() {
  const { student, isInCohort } = useAuth()
  const [units, setUnits] = useState<UnitWithProgress[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const isPremium = student?.subscription_status === 'active'
  const hasFullAccess = isPremium && isInCohort
  const isPaidWaiting = isPremium && !isInCohort

  useEffect(() => {
    if (student) {
      loadUnits()
    }
  }, [student])

  const loadUnits = async () => {
    if (!student) return

    setIsLoading(true)
    // Sync progress first (fixes stale lesson/unit counts)
    await syncStudentProgress(student.id)
    // student.id = PK of students table, matches FK and RLS in progress tables
    const { data } = await getUnitsWithProgress(student.id)
    if (data) {
      setUnits(data)
    }
    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Curso de Francés Intensivo
        </h1>
        <p className="text-gray-600">
          4 unidades de aprendizaje estructurado
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Trophy className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {units.filter(u => u.progress?.status === 'completed').length}
              </p>
              <p className="text-sm text-gray-500">Completadas</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100">
              <Star className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {student?.total_points || 0}
              </p>
              <p className="text-sm text-gray-500">Puntos</p>
            </div>
          </div>
        </div>
      </div>

      {/* Units Grid */}
      <div className="space-y-4">
        {units.map((unit) => {
          const status = unit.progress?.status || 'locked'
          const colors = statusColors[status]
          const isLocked = status === 'locked'
          const isContentLocked = !hasFullAccess && unit.order_num > 1

          if (isContentLocked) {
            return (
              <div
                key={unit.id}
                className="relative rounded-2xl border-2 border-dashed border-gray-300 overflow-hidden bg-gray-50/50"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-xl ${isPaidWaiting ? 'bg-blue-100' : 'bg-orange-100'}`}>
                        {isPaidWaiting ? (
                          <Clock className="w-5 h-5 text-blue-500" />
                        ) : (
                          <Lock className="w-5 h-5 text-orange-500" />
                        )}
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-400">
                          Unidad {unit.order_num}
                        </span>
                        <h3 className="text-xl font-bold text-gray-500">
                          {unit.title}
                        </h3>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${isPaidWaiting ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                      {isPaidWaiting ? 'Próximamente' : 'No disponible'}
                    </span>
                  </div>

                  <p className="text-gray-400 mb-4">
                    {unit.description || 'Sin descripción'}
                  </p>

                  {isPaidWaiting ? (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 text-center">
                      <p className="font-semibold text-blue-700">
                        Estamos preparando tu plataforma
                      </p>
                      <p className="text-sm text-blue-600/80">
                        En breve tendrás acceso completo.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl p-4 flex flex-col sm:flex-row items-center gap-4">
                      <div className="flex-1 text-center sm:text-left">
                        <p className="font-semibold text-orange-700">
                          Pasa a versión completa
                        </p>
                        <p className="text-sm text-orange-600/80">
                          Desbloquea las 4 unidades, talleres en vivo y certificado
                        </p>
                      </div>
                      <Link
                        to="/planes"
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors shrink-0"
                      >
                        Desbloquear
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )
          }

          return (
            <div
              key={unit.id}
              className={`relative rounded-2xl border-2 overflow-hidden transition-all ${colors.border} ${colors.bg} ${
                isLocked ? 'opacity-60' : 'hover:shadow-lg'
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${colors.bg}`}>
                      {statusIcons[status]}
                    </div>
                    <div>
                      <span className={`text-sm font-medium ${colors.text}`}>
                        Unidad {unit.order_num}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900">
                        {unit.title}
                      </h3>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}>
                    {statusLabels[status]}
                  </span>
                </div>

                <p className="text-gray-600 mb-4">
                  {unit.description || 'Sin descripción'}
                </p>

                {/* Progress Bar */}
                {unit.progress && status !== 'locked' && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Progreso</span>
                      <span className="font-medium">
                        {unit.progress.lessons_completed || 0} / {unit.progress.total_lessons || 4} lecciones
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${status === 'completed' ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${(unit.progress.total_lessons || 4) > 0 ? ((unit.progress.lessons_completed || 0) / (unit.progress.total_lessons || 4)) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                )}

                {!isLocked && (
                  <Link
                    to={`/unit/${unit.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {status === 'available' ? 'Comenzar' : 'Continuar'}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {units.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="font-semibold mb-2">No hay unidades disponibles</h3>
          <p className="text-sm text-gray-500">
            Las unidades se cargaran pronto.
          </p>
        </div>
      )}
    </div>
  )
}
